import type { ReactNode } from 'react';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  Container,
  Grid,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Link as RouterLink } from 'react-router-dom';

type ReportDefinition = {
  title: string;
  category: string;
  description: string;
  path: string;
  icon: ReactNode;
  accent: string;
  tint: string;
};

const reports: ReportDefinition[] = [
  {
    title: 'Pipeline Report',
    category: 'Sales',
    description: 'Review open opportunities by stage, status, company, and contact.',
    path: '/reports/pipeline',
    icon: <TrendingUpOutlinedIcon />,
    accent: '#2563eb',
    tint: '#eff6ff'
  },
  {
    title: 'Won Opportunities',
    category: 'Performance',
    description: 'Track closed-won opportunities and recent sales performance.',
    path: '/reports/won',
    icon: <EmojiEventsOutlinedIcon />,
    accent: '#15803d',
    tint: '#f0fdf4'
  },
  {
    title: 'Contacts Report',
    category: 'Relationships',
    description: 'Filter and export contacts by status and relationship details.',
    path: '/reports/contacts',
    icon: <PeopleAltOutlinedIcon />,
    accent: '#4f46e5',
    tint: '#eef2ff'
  },
  {
    title: 'Introductions',
    category: 'Networking',
    description: 'See introductions made across your network and when they occurred.',
    path: '/reports/introductions',
    icon: <HandshakeOutlinedIcon />,
    accent: '#7c3aed',
    tint: '#f5f3ff'
  },
  {
    title: 'Lost But Tracked',
    category: 'Follow-up',
    description: 'Monitor lost opportunities that remain active follow-up candidates.',
    path: '/reports/lost-but-tracked',
    icon: <TrackChangesOutlinedIcon />,
    accent: '#b45309',
    tint: '#fffbeb'
  },
  {
    title: 'Events Report',
    category: 'Activity',
    description: 'Review event activity, dates, speakers, and attendance context.',
    path: '/reports/events',
    icon: <EventOutlinedIcon />,
    accent: '#0f766e',
    tint: '#f0fdfa'
  }
];

const ReportsLandingPage = () => (
  <Container maxWidth="lg">
    <Breadcrumbs sx={{ mt: 2 }}>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
        Dashboard
      </MuiLink>
      <Typography color="text.primary">Reports</Typography>
    </Breadcrumbs>

    <Box sx={{ mt: 3, mb: 3.5 }}>
      <Typography
        variant="overline"
        sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
      >
        Analytics
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 750, letterSpacing: '-0.025em' }}>
        Reports
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 640, lineHeight: 1.65 }}>
        Open a report to review activity, performance, and relationship data across Forge.
      </Typography>
    </Box>

    <Grid container spacing={2.25} sx={{ pb: 5 }}>
      {reports.map((report) => (
        <Grid key={report.path} size={{ xs: 12, sm: 6, lg: 4 }}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              minHeight: 228,
              position: 'relative',
              overflow: 'hidden',
              borderColor: 'divider',
              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '0 auto 0 0',
                width: 4,
                bgcolor: report.accent,
                zIndex: 1
              },
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: report.accent,
                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.09)'
              }
            }}
          >
            <CardActionArea
              component={RouterLink}
              to={report.path}
              aria-label={`Open ${report.title}`}
              sx={{
                height: '100%',
                p: 2.5,
                pl: 2.75,
                display: 'flex',
                alignItems: 'stretch'
              }}
            >
              <Stack sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2
                  }}
                >
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: report.tint,
                      color: report.accent,
                      borderRadius: 2
                    }}
                  >
                    {report.icon}
                  </Avatar>
                  <Typography
                    variant="caption"
                    sx={{
                      color: report.accent,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase'
                    }}
                  >
                    {report.category}
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ mt: 2, fontWeight: 750, lineHeight: 1.3 }}>
                  {report.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.75, lineHeight: 1.6, flexGrow: 1 }}
                >
                  {report.description}
                </Typography>

                <Box
                  sx={{
                    mt: 2.25,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: report.accent
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>
                    Open report
                  </Typography>
                  <ArrowForwardRoundedIcon fontSize="small" />
                </Box>
              </Stack>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Container>
);

export default ReportsLandingPage;
